import { requireSession, readSession, operationId, emitStorageChange } from './submissionSession';

const DB_NAME = 'ranger-mdt-offline';
const DB_VERSION = 4;
const INCIDENTS = 'incidents', QUEUE = 'sync-queue', CACHE = 'cached-data', META = 'metadata';
const UPLOADS = 'video-uploads';
let db = null, opening = null;

export function initDB() {
  if (db) return Promise.resolve(db);
  if (opening) return opening;
  opening = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onblocked = () => window.dispatchEvent(new Event('cad-storage-blocked'));
    request.onupgradeneeded = event => {
      const database = request.result, transaction = request.transaction;
      for (const [name, keyPath] of [[INCIDENTS, 'localId'], [QUEUE, 'id']]) {
        const store = database.objectStoreNames.contains(name) ? transaction.objectStore(name)
          : database.createObjectStore(name, { keyPath, autoIncrement: true });
        if (!store.indexNames.contains('owner')) store.createIndex('owner', 'ownerUserId');
        if (name === INCIDENTS && !store.indexNames.contains('submission')) store.createIndex('submission', 'submissionKey', { unique: true });
        if (name === INCIDENTS && !store.indexNames.contains('intent')) store.createIndex('intent', 'intentKey', { unique: true });
        if (event.oldVersion === 1) {
          // Unknown ownership must never be inferred from the next login.
          const cursor = store.openCursor();
          cursor.onsuccess = () => {
            const entry = cursor.result;
            if (entry) {
              const original = entry.value;
              entry.update({ ...original, legacyOriginal: original, ownerUserId: null,
                syncStatus: 'quarantined', status: 'quarantined', recoveryStatus: 'unassigned', schemaVersion: 1 });
              entry.continue();
            }
          };
        }
      }
      if (!database.objectStoreNames.contains(CACHE)) database.createObjectStore(CACHE, { keyPath: 'key' });
      if (!database.objectStoreNames.contains(META)) database.createObjectStore(META, { keyPath: 'key' });
      if (!database.objectStoreNames.contains(UPLOADS)) {
        const uploads = database.createObjectStore(UPLOADS, { keyPath: 'localId', autoIncrement: true });
        uploads.createIndex('owner', 'ownerUserId');
        uploads.createIndex('submission', 'submissionKey', { unique: true });
      }
    };
    request.onsuccess = () => {
      db = request.result;
      db.onversionchange = () => { db.close(); db = null; opening = null; };
      resolve(db);
    };
  }).catch(error => { opening = null; throw error; });
  return opening;
}

// Only transaction completion acknowledges a local save; request success can still abort.
async function transaction(names, mode, work) {
  const database = await initDB();
  return new Promise((resolve, reject) => {
    const tx = database.transaction(names, mode);
    let result;
    tx.oncomplete = () => { if (mode === 'readwrite') emitStorageChange(); resolve(result); };
    tx.onerror = tx.onabort = () => reject(tx.error || new Error('Local save was not committed. Keep this form open and retry.'));
    try { work(tx, value => { result = value; }, error => { tx.abort(); reject(error); }); }
    catch (error) { tx.abort(); reject(error); }
  });
}
const owner = explicit => explicit ?? readSession()?.ownerUserId ?? null;
const list = (name, ownerId) => transaction([name], 'readonly', (tx, done) => {
  if (!ownerId) { done([]); return; }
  const request = tx.objectStore(name).index('owner').getAll(ownerId);
  request.onsuccess = () => done(request.result);
});
function update(name, id, ownerId, change) {
  return transaction([name], 'readwrite', (tx, done, fail) => {
    const store = tx.objectStore(name), request = store.get(id);
    request.onsuccess = () => {
      const row = request.result;
      if (!row || row.ownerUserId !== ownerId) { tx.abort(); return; }
      try {
        const changed = change(row);
        if (!changed) { done(null); return; }
        store.put(changed); done(changed);
      } catch (error) { fail(error); }
    };
  });
}

export function canonicalCall(data, session) {
  if (data.ownerUserId != null && Number(data.ownerUserId) !== session.ownerUserId)
    throw new Error('This call belongs to another account. Sign in as its owner.');
  const kind = data.submissionKind || 'manual';
  if (!['manual', 'backdated', 'waze'].includes(kind)) throw new Error('Unknown call creation workflow. Keep this work for review.');
  if (kind === 'backdated' && (data.cadId || data.videos?.length))
    throw new Error('Backdated calls allocate their CAD reference automatically. Attach footage after the call is confirmed.');
  const date = data.occurredAtUtc || data.reportedAt || data.offlineCreatedAt || data.createdAt;
  if (!date || !Number.isFinite(new Date(date).getTime())) throw new Error('A valid reported occurrence time is required.');
  if (kind === 'waze') {
    if (!Number.isSafeInteger(data.wazeAlertId) || data.wazeAlertId <= 0) throw new Error('A valid Waze alert identifier is required.');
    const snapshot = { id: data.wazeAlertId }, raw = data.wazeAlertSnapshot || {};
    for (const key of ['uuid', 'type', 'subtype', 'publishedAtUtc', 'latitude', 'longitude', 'street', 'city', 'reportDescription', 'magvar'])
      if (raw[key] === null || ['string', 'number', 'boolean'].includes(typeof raw[key])) snapshot[key] = raw[key];
    return JSON.parse(JSON.stringify({ submissionKind: kind, wazeAlertId: data.wazeAlertId,
      occurredAtUtc: new Date(date).toISOString(), onScene: data.onScene === true,
      incidentType: data.incidentType, notes: data.notes, trafficDirection: data.trafficDirection,
      wazeAlertSnapshot: snapshot, ownerUserId: session.ownerUserId }));
  }
  return JSON.parse(JSON.stringify({
    submissionKind: kind === 'backdated' ? kind : undefined,
    cadId: data.cadId || undefined, incidentType: data.incidentType || data.type,
    occurredAtUtc: new Date(date).toISOString(), occurredEndUtc: data.occurredEndUtc,
    latitude: data.latitude, longitude: data.longitude,
    locationDescription: data.locationDescription || data.location || data.freeway,
    streetName: data.streetName, trafficDirection: data.trafficDirection,
    primaryRangerId: kind === 'backdated' ? data.primaryRangerId : data.primaryRangerId || session.profile.rangerId,
    notes: data.notes ?? data.description, vehicle: data.vehicle,
    servicesProvided: data.servicesProvided || [], participantRangerIds: data.participantRangerIds || [], videos: data.videos || [],
    source: data.source, ownerUserId: session.ownerUserId
  }));
}

export async function saveOfflineIncident(data, token, uploadInputs = []) {
  const session = requireSession(token), payload = canonicalCall(data, session);
  const operation = data.operationId || operationId();
  const uploads = uploadInputs.length ? canonicalUploadInputs(uploadInputs, session, false) : [];
  return transaction([INCIDENTS, META, ...(uploads.length ? [UPLOADS] : [])], 'readwrite', (tx, done, fail) => {
    const meta = tx.objectStore(META), getDevice = meta.get('device-id');
    getDevice.onsuccess = () => {
      const deviceId = getDevice.result?.value || operationId();
      if (!getDevice.result) meta.put({ key: 'device-id', value: deviceId });
      const finish = row => {
        if (!uploads.length) { done(row); return; }
        writeUploadRows(tx, uploads.map(input => ({ ...input, incidentLocalId: row.localId })), session, deviceId,
          saved => done({ ...row, uploadLocalIds: saved.map(upload => upload.localId) }), fail);
      };
      const store = tx.objectStore(INCIDENTS), submissionKey = `${session.ownerUserId}:${deviceId}:${operation}`;
      const intentKey = payload.submissionKind === 'waze' ? `${session.ownerUserId}:${deviceId}:waze:${payload.wazeAlertId}` : null;
      const lookup = intentKey ? store.index('intent').get(intentKey) : store.index('submission').get(submissionKey);
      lookup.onsuccess = () => {
        if (lookup.result) {
          // One immutable conversion intent per owner/device/alert, including after review.
          // A repeated tap resumes the original captured time/details rather than replacing them.
          if (intentKey) { finish(lookup.result); return; }
          if (JSON.stringify(lookup.result.payload) !== JSON.stringify({ ...payload, deviceId, operationId: operation })) { tx.abort(); return; }
          finish(lookup.result); return;
        }
        const row = { schemaVersion: 2, ownerUserId: session.ownerUserId, deviceId, operationId: operation,
          ...(intentKey ? { intentKey } : {}),
          submissionKey, payload: { ...payload, deviceId, operationId: operation }, syncStatus: 'pending',
          createdAt: new Date().toISOString(), lastModified: new Date().toISOString(), attempts: 0 };
        const saved = store.add(row); saved.onsuccess = () => finish({ ...row, localId: saved.result });
      };
    };
  });
}
export const getOfflineIncidents = ownerId => list(INCIDENTS, owner(ownerId));
export const getPendingIncidents = async ownerId => (await getOfflineIncidents(ownerId)).filter(row =>
  row.schemaVersion === 2 && ['pending', 'error', 'syncing'].includes(row.syncStatus) &&
  !(row.leaseUntil > Date.now()) && !(row.nextAttemptAt > Date.now()));
export const getLocalIncident = async (id, ownerId) => (await getOfflineIncidents(ownerId)).find(row => row.localId === id) || null;
export const claimIncident = (id, ownerId, leaseOwner) => update(INCIDENTS, id, ownerId, row => {
  if (row.schemaVersion !== 2 || row.syncStatus === 'synced' || row.syncStatus === 'needs-review' || row.leaseUntil > Date.now()) return null;
  return { ...row, syncStatus: 'syncing', leaseOwner, leaseUntil: Date.now() + 30000, attempts: (row.attempts || 0) + 1 };
});
export const finishIncident = (id, ownerId, leaseOwner, changes) => update(INCIDENTS, id, ownerId, row =>
  row.leaseOwner !== leaseOwner ? null : { ...row, ...changes, leaseOwner: null, leaseUntil: 0, lastModified: new Date().toISOString() });
export const updateIncidentSyncStatus = (id, status, serverId = null, ownerId) => update(INCIDENTS, id, owner(ownerId), row =>
  ({ ...row, syncStatus: status, ...(serverId ? { serverId } : {}), lastModified: new Date().toISOString() }));
export const deleteLocalIncident = (id, ownerId) => transaction([INCIDENTS, UPLOADS], 'readwrite', (tx, done) => {
  const store = tx.objectStore(INCIDENTS), request = store.get(id);
  request.onsuccess = () => {
    if (request.result?.ownerUserId !== owner(ownerId) || request.result?.syncStatus !== 'synced') { tx.abort(); return; }
    const uploads = tx.objectStore(UPLOADS).index('owner').getAll(owner(ownerId));
    uploads.onsuccess = () => {
      if (uploads.result.some(row => row.incidentLocalId === id && !row.payload.incidentId)) { done(false); return; }
      store.delete(id); done(true);
    };
  };
});
export async function clearSyncedIncidents() {
  const rows = (await getOfflineIncidents()).filter(row => row.syncStatus === 'synced'); let removed = 0;
  for (const row of rows) if (await deleteLocalIncident(row.localId)) removed++;
  return removed;
}

export async function addToSyncQueue(operation, token) {
  const session = requireSession(token);
  return transaction([QUEUE], 'readwrite', (tx, done) => {
    const row = { ...operation, ownerUserId: session.ownerUserId, schemaVersion: 2, timestamp: new Date().toISOString(),
      // Only deterministic updates can be retried automatically. Other work is retained for review.
      status: operation.method === 'PUT' ? 'pending' : 'needs-review', retryCount: 0 };
    const request = tx.objectStore(QUEUE).add(row); request.onsuccess = () => done({ ...row, id: request.result });
  });
}
export const getPendingSyncOperations = async ownerId => (await list(QUEUE, owner(ownerId))).filter(row =>
  row.schemaVersion === 2 && ['pending', 'failed', 'processing'].includes(row.status));
export const getOwnedOperations = ownerId => list(QUEUE, owner(ownerId));
export const updateSyncQueueItem = (id, changes, ownerId) => update(QUEUE, id, owner(ownerId), row => ({ ...row, ...changes }));
export const clearCompletedSyncItems = async () => {
  // Retain completion receipts until an explicit owner cleanup; never erase pending work.
  return 0;
};

export async function cacheData(key, data, ownerId) {
  const account = owner(ownerId); if (!account) return null;
  return transaction([CACHE], 'readwrite', (tx, done) => {
    const row = { key: `${account}:${key}`, ownerUserId: account, data, cachedAt: new Date().toISOString() };
    tx.objectStore(CACHE).put(row); done(row);
  });
}
export const getCachedData = async (key, ownerId) => {
  const account = owner(ownerId); if (!account) return null;
  return transaction([CACHE], 'readonly', (tx, done) => {
    const request = tx.objectStore(CACHE).get(`${account}:${key}`); request.onsuccess = () => done(request.result?.data ?? null);
  });
};
export const clearCache = () => transaction([CACHE], 'readwrite', (tx, done) => {
  const request = tx.objectStore(CACHE).openCursor(), account = owner();
  request.onsuccess = () => { const cursor = request.result; if (cursor) { if (cursor.value.ownerUserId === account) cursor.delete(); cursor.continue(); } else done(true); };
});
export async function getSyncStats() {
  const account = owner();
  const rows = await getOfflineIncidents(account), operations = await getOwnedOperations(account);
  if (owner() !== account) return { pendingIncidents: 0, syncingIncidents: 0, syncedIncidents: 0, errorIncidents: 0, queuedOperations: 0, totalLocalIncidents: 0 };
  return { pendingIncidents: rows.filter(row => row.syncStatus === 'pending').length,
    syncingIncidents: rows.filter(row => row.syncStatus === 'syncing').length,
    syncedIncidents: rows.filter(row => row.syncStatus === 'synced').length,
    errorIncidents: rows.filter(row => ['error', 'needs-review'].includes(row.syncStatus)).length,
    queuedOperations: operations.filter(row => row.status !== 'completed').length, totalLocalIncidents: rows.length };
}
export const isOfflineStorageAvailable = () => typeof indexedDB !== 'undefined';

// Save metadata only. Large recordings remain at their original device location;
// a restarted browser asks for the original file instead of silently losing it.
function canonicalUploadInputs(inputs, session, targetRequired = true) {
  if (!Array.isArray(inputs) || !inputs.length) throw new Error('Select at least one recording.');
  return inputs.map(input => {
    if (Number(input.ownerUserId) !== session.ownerUserId) throw new Error('These recordings belong to another account. Sign in as their owner.');
    const file = input.file;
    if (!file || typeof file.name !== 'string' || !file.name || !Number.isSafeInteger(file.size) || file.size <= 0)
      throw new Error('Select a nonempty original recording.');
    if (targetRequired && (!Number.isSafeInteger(input.incidentId) || input.incidentId <= 0)) throw new Error('Confirm the call before saving its recording upload.');
    const timestamp = new Date(input.timestamp);
    if (!Number.isFinite(timestamp.getTime())) throw new Error('A valid recording timestamp is required.');
    if (typeof input.operationId !== 'string' || !/^[A-Za-z0-9._:-]{1,64}$/.test(input.operationId)) throw new Error('Recording upload identity is invalid.');
    return { operationId: input.operationId, file: { name: file.name, size: file.size, lastModified: Number(file.lastModified) || 0 },
      payload: { incidentId: targetRequired ? input.incidentId : null, cameraType: input.cameraType || 'unspecified', incidentType: input.incidentType || 'unspecified',
        timestamp: timestamp.toISOString(), notes: input.notes || '' } };
  });
}
function writeUploadRows(tx, rows, session, deviceId, done, fail) {
  const store = tx.objectStore(UPLOADS), saved = new Array(rows.length); let remaining = rows.length;
  rows.forEach((input, index) => {
    const submissionKey = `${session.ownerUserId}:${deviceId}:${input.operationId}`, request = store.index('submission').get(submissionKey);
    request.onsuccess = () => {
      const original = request.result;
      if (original) {
        if (JSON.stringify(original.file) !== JSON.stringify(input.file) || JSON.stringify(original.payload) !== JSON.stringify(input.payload) ||
            (input.incidentLocalId && input.incidentLocalId !== original.incidentLocalId)) {
          fail(new Error('This recording already has saved upload details. Use Upload recovery to finish its original call association.')); return;
        }
        saved[index] = original; if (--remaining === 0) done(saved); return;
      }
      const row = { ...input, schemaVersion: 1, ownerUserId: session.ownerUserId, deviceId, submissionKey,
        status: input.incidentLocalId ? 'awaiting-call' : 'pending', attempts: 0, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
      const add = store.add(row); add.onsuccess = () => { saved[index] = { ...row, localId: add.result }; if (--remaining === 0) done(saved); };
    };
  });
}
export async function saveUploadBatch(inputs, token) {
  const session = requireSession(token), rows = canonicalUploadInputs(inputs, session);
  return transaction([UPLOADS, META], 'readwrite', (tx, done, fail) => {
    const metadata = tx.objectStore(META), lookup = metadata.get('device-id');
    lookup.onsuccess = () => {
      const deviceId = lookup.result?.value || operationId();
      if (!lookup.result) metadata.put({ key: 'device-id', value: deviceId });
      writeUploadRows(tx, rows, session, deviceId, done, fail);
    };
  });
}
export function bindUploadTarget(id, ownerId) {
  return transaction([UPLOADS, INCIDENTS], 'readwrite', (tx, done, fail) => {
    const store = tx.objectStore(UPLOADS), get = store.get(id);
    get.onsuccess = () => {
      const row = get.result;
      if (!row || row.ownerUserId !== ownerId) { fail(new Error('This recording belongs to another account.')); return; }
      if (row.payload.incidentId) { done(row); return; }
      const call = tx.objectStore(INCIDENTS).get(row.incidentLocalId);
      call.onsuccess = () => {
        const source = call.result;
        if (!source || source.ownerUserId !== ownerId || source.syncStatus !== 'synced' || !Number.isSafeInteger(source.serverId) || source.serverId <= 0) { done(null); return; }
        const bound = { ...row, payload: { ...row.payload, incidentId: source.serverId }, targetBoundAt: new Date().toISOString() };
        store.put(bound); done(bound);
      };
    };
  });
}
export const getUploadRecords = ownerId => list(UPLOADS, owner(ownerId));
export const getUploadRecord = async (id, ownerId) => (await getUploadRecords(ownerId)).find(row => row.localId === id) || null;
export const claimUpload = (id, ownerId, leaseOwner) => update(UPLOADS, id, ownerId, row =>
  row.status === 'accepted' || row.leaseUntil > Date.now() ? null : { ...row, leaseOwner, leaseUntil: Date.now() + 120000, status: 'checking', attempts: row.attempts + 1 });
export const finishUpload = (id, ownerId, leaseOwner, changes) => update(UPLOADS, id, ownerId, row =>
  row.leaseOwner !== leaseOwner ? null : { ...row, ...changes, leaseOwner: null, leaseUntil: 0, updatedAt: new Date().toISOString() });
export const renewUpload = (id, ownerId, leaseOwner) => update(UPLOADS, id, ownerId, row =>
  row.leaseOwner !== leaseOwner || row.status === 'accepted' ? null : { ...row, leaseUntil: Date.now() + 120000 });

export async function getLegacyRecords(token) {
  const session = requireSession(token);
  if (session.profile.role?.toLowerCase() !== 'admin') return [];
  return transaction([INCIDENTS, QUEUE], 'readonly', (tx, done) => {
    const rows = []; let finished = 0;
    [INCIDENTS, QUEUE].forEach(name => {
      const request = tx.objectStore(name).getAll(); request.onsuccess = () => {
        rows.push(...request.result.filter(row => row.schemaVersion !== 2).map(row => ({ store: name, record: row })));
        if (++finished === 2) done(rows);
      };
    });
  });
}
export function assignLegacyIncident(id, ownerUserId, token) {
  const admin = requireSession(token);
  if (admin.profile.role?.toLowerCase() !== 'admin' || !Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) throw new Error('Administrator review and a valid owner are required.');
  return transaction([INCIDENTS], 'readwrite', (tx, done) => {
    const store = tx.objectStore(INCIDENTS), request = store.get(id);
    request.onsuccess = () => {
      const row = request.result;
      if (!row || row.schemaVersion === 2) { tx.abort(); return; }
      const receipt = row.legacyOriginal?.syncStatus === 'synced' && row.legacyOriginal?.serverId;
      const changed = { ...row, ownerUserId, recoveryStatus: receipt ? 'retained-receipt' : 'owner-review', syncStatus: receipt ? 'synced' : 'needs-review', assignedByUserId: admin.ownerUserId };
      store.put(changed); done(changed);
    };
  });
}
export async function approveRecoveredIncident(id, data, token) {
  const session = requireSession(token), payload = canonicalCall(data, session);
  return transaction([INCIDENTS, META], 'readwrite', (tx, done) => {
    const store = tx.objectStore(INCIDENTS), request = store.get(id);
    request.onsuccess = () => {
      const original = request.result;
      if (!original || original.ownerUserId !== session.ownerUserId || original.recoveryStatus !== 'owner-review') { tx.abort(); return; }
      const meta = tx.objectStore(META), lookup = meta.get('device-id');
      lookup.onsuccess = () => {
        const deviceId = lookup.result?.value || operationId(), operation = `legacy-${id}`;
        if (!lookup.result) meta.put({ key: 'device-id', value: deviceId });
        const changed = { ...original, schemaVersion: 2, recoveryStatus: 'recovered', deviceId, operationId: operation,
          submissionKey: `${session.ownerUserId}:${deviceId}:${operation}`, payload: { ...payload, deviceId, operationId: operation },
          syncStatus: 'pending', nextAttemptAt: 0, leaseUntil: 0, attempts: 0 };
        store.put(changed); done(changed);
      };
    };
  });
}
export const retryLocalIncident = (id, token) => {
  const session = requireSession(token);
  return update(INCIDENTS, id, session.ownerUserId, row => row.schemaVersion === 2 && row.syncStatus !== 'synced'
    ? { ...row, syncStatus: 'pending', nextAttemptAt: 0, lastError: null } : null);
};
export const pauseLocalIncident = (id, token) => {
  const session = requireSession(token);
  return update(INCIDENTS, id, session.ownerUserId, row => row.schemaVersion === 2 && row.syncStatus !== 'synced' && !(row.leaseUntil > Date.now())
    ? { ...row, syncStatus: 'needs-review', lastError: 'Delivery paused. Check CAD before entering a replacement call.' } : null);
};
export function reviseRejectedIncident(id, data, token) {
  const session = requireSession(token);
  return update(INCIDENTS, id, session.ownerUserId, row => {
    if (![400, 422].includes(row.lastHttpStatus) || row.syncStatus !== 'needs-review' || row.leaseUntil > Date.now())
      throw new Error('This call is confirmed or delivery may still be in progress. Keep its saved identifier and wait for confirmation before changing it.');
    const payload = canonicalCall({ ...data, submissionKind: row.payload?.submissionKind }, session);
    const operation = operationId();
    return { ...row, requestHistory: [...(row.requestHistory || []), row.payload], operationId: operation,
      submissionKey: `${session.ownerUserId}:${row.deviceId}:${operation}`, payload: { ...payload, deviceId: row.deviceId, operationId: operation },
      syncStatus: 'pending', leaseUntil: 0, nextAttemptAt: 0, lastError: null, lastHttpStatus: null };
  });
}
